import React, { useState, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import { FormControl, Select, MenuItem, ListItemText } from '@mui/material'

import { Checkbox, CircularProgress } from '@mui/material'

import { filterInputStyles } from './filter-input.styles'

/**
 * @typedef {object} FilterOption
 * @property {string | number} key - El identificador único de la opción.
 * @property {string} value - El texto legible que se mostrará para la opción.
 */

/**
 * @typedef {object} FilterChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop `name`.
 * @property {string | number | Array<string|number>} target.value - El valor seleccionado. Si `multiple` es `true`, será un array de claves; de lo contrario, será una única clave o una cadena vacía.
 */

/**
 * @typedef {object} FilterInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {FilterOption[]} [values=[]] - Un array de opciones disponibles para seleccionar.
 * @property {boolean} [multiple=false] - Si es `true`, permite la selección de múltiples valores con checkboxes.
 * @property {string} [defaultFilter] - El texto a mostrar cuando no hay ninguna opción seleccionada. Si no se proporciona, se usa el mensaje i18n 'global.all'.
 * @property {object} [MenuProps] - Props adicionales a pasar al componente `Menu` de Material-UI.
 * @property {string} name - El nombre del campo, que se incluirá en el objeto de evento sintético del `onChange`.
 * @property {string} label - La etiqueta que se muestra antes del valor seleccionado.
 * @property {string | number} [value] - El valor actualmente seleccionado. Si `multiple` es `true`, debe ser una cadena de claves separadas por comas (ej. '1,2,3').
 * @property {function(FilterChangeEvent): void} onChange - La función callback que se ejecuta cuando la selección cambia.
 * @property {boolean} [required] - Si es `true`, el campo se marca como requerido.
 * @property {boolean} [disabled] - Si es `true`, el campo estará deshabilitado.
 */

/**
 * Un componente de selector desplegable especializado para filtros, construido sobre `Select` de Material-UI.
 * 
 * Soporta selección simple o múltiple, mostrando checkboxes en modo múltiple. En modo simple,
 * incluye automáticamente una opción "Todos" que representa ninguna selección. El valor seleccionado
 * se muestra inline junto a la etiqueta del filtro.
 * 
 * En modo múltiple, el componente gestiona el estado interno de las selecciones y solo dispara
 * el evento `onChange` cuando se cierra el menú, para mejorar el rendimiento.
 * 
 * **Nota:** Este componente requiere `react-intl` para la internacionalización.
 * 
 * @component
 * @example
 * const MyFilterPanel = () => {
 *   const [status, setStatus] = useState('');
 * 
 *   const statusOptions = [
 *     { key: 'active', value: 'Activo' },
 *     { key: 'pending', value: 'Pendiente' },
 *     { key: 'inactive', value: 'Inactivo' },
 *   ];
 * 
 *   const handleChange = (e) => {
 *     console.log(`Campo: ${e.target.name}, Valor: ${e.target.value}`);
 *     setStatus(e.target.value);
 *   };
 * 
 *   // Este componente debe estar envuelto en un <IntlProvider>.
 *   return (
 *     <FilterInput
 *       name="statusFilter"
 *       label="Estado"
 *       values={statusOptions}
 *       value={status}
 *       onChange={handleChange}
 *     />
 *   );
 * };
 * 
 * @example
 * // Modo múltiple
 * <FilterInput
 *   name="categoriesFilter"
 *   label="Categorías"
 *   values={categoryOptions}
 *   value="1,3,5"
 *   onChange={handleChange}
 *   multiple
 * />
 * 
 * @param {FilterInputProps} props - Las props del componente FilterInput.
 */
export const FilterInput = withStyles(filterInputStyles)(({
  classes,
  values = [],
  multiple = false,
  defaultFilter,
  MenuProps,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const value = useMemo(
    () => (multiple ? (props.value ? props.value.split(',') : []) : props.value || ''),
    [multiple, props.value]
  )

  const defaultFilterValue = defaultFilter ? defaultFilter : formatMessage({ id: 'global.all' })

  const [selectedValues, setSelectedValues] = useState([])

  useEffect(() => {
    setSelectedValues(value)
  }, [value])

  const handleChange = (e) => {
    if (multiple) {
      setSelectedValues(e.target.value)
    } else props.onChange(e)
  }

  if (!values || values.length === 0) {
    return null
  }

  const getSelectOptions = () => {
    const options = values.map((item, i) => (
      <MenuItem key={i} value={item.key}>
        {multiple ? (
          <>
            <Checkbox checked={selectedValues.indexOf(item.key) > -1} />
            <ListItemText primary={item.value} />
          </>
        ) : (
          <span>{item.value}</span>
        )}
      </MenuItem>
    ))
    if (!multiple)
      options.unshift(
        <MenuItem key="all" value="">
          {defaultFilterValue}
        </MenuItem>
      )
    return options
  }

  const getRenderValue = (selectedValue) => {
    return (
      <span>
        {props.label}:
        <span className={classes.inlineValue}>
          {values.length ? (
            (!Array.isArray(selectedValue) && selectedValue !== '') ||
            (Array.isArray(selectedValue) && selectedValue.length) ? (
              multiple ? (
                selectedValues
                  .map((valueSelected) => values.find(({ key }) => key === valueSelected))
                  .filter((el) => el)
                  .map((el) => el.value)
                  .join(', ')
              ) : (
                values.find(({ key }) => key === selectedValue)?.value
              )
            ) : (
              defaultFilterValue
            )
          ) : (
            <CircularProgress size={14} />
          )}
        </span>
      </span>
    )
  }

  const onCloseProps = multiple
    ? { onClose: () => props.onChange({ target: { name: props.name, value: selectedValues } }) }
    : {}

  return (
    <FormControl
      variant="outlined"
      className={classes.root}
      required={props.required}
      disabled={props.disabled}
    >
      <Select
        autoWidth
        className={classes.input}
        name={props.name}
        value={multiple ? selectedValues : value}
        multiple={multiple}
        onChange={handleChange}
        {...onCloseProps}
        displayEmpty
        renderValue={getRenderValue}
        MenuProps={MenuProps}
      >
        {getSelectOptions()}
      </Select>
    </FormControl>
  )
})
